/**
 * Trade License Module Documentation
 * Process documentation for Demand Generation and Finance Integration
 */

export interface TreeNode {
  id: string;
  label: string;
  children?: TreeNode[];
  content?: string;
}

export const tradeLicenseDocumentation: TreeNode[] = [
  {
    id: 'overview',
    label: 'Overview',
    content: `# Trade License Demand Generation Process

This document describes the process of generating demands for trade licenses and how it integrates with the finance module.

## What is Demand Generation?

Demand generation is the process of calculating and creating annual license fees for trade license holders. When demand is generated:

1. The system calculates the fee based on the license category and trade area
2. A demand record is created for the license
3. A finance voucher is posted to the General Ledger
4. The demand appears in the DCB (Demand-Collection-Balance) reports`
  },
  {
    id: 'demand-generation-process',
    label: 'Demand Generation Process',
    content: `# Demand Generation Process

## Step 1: Select Installment Year

Choose the financial year for which demand should be generated (e.g., 2026-27).

## Step 2: View Available Licenses

The system displays all active licenses that are eligible for demand generation. Licenses that already have demand for the selected year are highlighted in green.

## Step 3: Calculate Preview

Before generating demand, you can preview the calculated amounts. The system uses the fee matrix to determine:

- Which fee slabs apply based on trade area
- The rate type (Flat, Percentage, or Per Unit)
- The final demand amount

## Step 4: Generate Demand

Select the licenses and click "Generate Demand". For each license:

1. Fee is calculated based on the fee matrix
2. Demand record is created in the database
3. Finance voucher is automatically posted (if enabled)
4. License validity period is updated

## Step 5: Review Results

After completion, you can see:
- How many licenses were processed successfully
- Any failures with error details
- Generated voucher numbers for finance tracking`
  },
  {
    id: 'fee-calculation',
    label: 'Fee Calculation',
    content: `# Fee Calculation Process

## How Fees are Calculated

The fee calculation depends on the rate type configured in the fee matrix:

### Flat Rate

A fixed amount based on the trade area range. For example:
- Trade area 0-100 sq.mt: Rs. 3,000
- Trade area 100-200 sq.mt: Rs. 5,000
- Trade area 200-500 sq.mt: Rs. 8,000

### Percentage Rate

A percentage of the trade area/investment value. For example:
- 0.5% of investment amount

### Per Unit Rate

A rate multiplied by the number of units. For example:
- Rs. 25 per sq.mt of trade area

## Multiple Fee Types

A single demand may include multiple fee types:
- License Fee
- Renewal Fee
- Processing Fee

All applicable fees are calculated and summed to get the total demand amount.`
  },
  {
    id: 'finance-integration',
    label: 'Finance Integration',
    content: `# Finance Integration Process

## Automatic GL Posting

When demand is generated, the system automatically creates finance vouchers to record the receivable.

## Double-Entry Accounting

Each demand creates two accounting entries:

**DEBIT Entry:**
Account: Sundry Debtors - Trade License
Amount: Demand Amount

**CREDIT Entry:**
Account: Trade License Revenue
Amount: Demand Amount

## Voucher Creation Flow

1. Demand is successfully generated for a license
2. System checks if GL posting is enabled
3. Voucher header is created with unique voucher number
4. Two GL entries are created (debit and credit)
5. License is linked to the voucher for tracking

## Voucher Tracking

After demand generation, you can:
- View vouchers linked to each license
- See the GL breakdown (debit/credit entries)
- Track the voucher status in finance reports

## Important Notes

- Voucher creation failure does not fail the demand generation
- If voucher fails, demand is still marked as successful
- Finance team can manually create vouchers if needed`
  },
  {
    id: 'process-flow-diagram',
    label: 'Process Flow',
    content: `# Complete Process Flow

## Demand Generation with Finance Integration

\`\`\`
User selects installment year
        |
        v
View available licenses
        |
        v
Calculate demand preview (optional)
        |
        v
Select licenses and generate demand
        |
        v
For each license:
        |
        +------------------------+
        v                        v
Calculate fee amount      Create demand record
(using fee matrix)        (in database)
        |                        |
        +------------------------+
                    |
                    v
        Finance Integration
                    |
                    v
        Check: GL posting enabled?
                    |
            Yes     |     No
                    |      \`--> Skip voucher
                    v
        Create voucher header
                    |
                    v
        Create GL entries:
        - DEBIT: Receivable
        - CREDIT: Revenue
                    |
                    v
        Link voucher to license
                    |
                    v
        Complete demand generation
                    |
                    v
        View results and voucher numbers
\`\`\``
  }
];

export default tradeLicenseDocumentation;
